// 261010 P9D D5 (EV29–32) — thanh cấu trúc video ở bước 1: Cắt đôi / Gộp với video kế / Chèn video trống / Xoá. Gọi THẲNG route có sẵn
// (`split` / `merge-next` / `insert-after` / `DELETE events/:id`), không đi qua nháp `workflowEditOps`. Luật thuần ở `workflowStructOps.ts`.
// Đang sửa nháp ⇒ tắt (lý do rõ). Xong ⇒ `onActionDone` (trang tải lại); xoá / gộp có thể làm mất chính video đang xem ⇒ trang tự hiện «video không còn».
import React, { useMemo, useState } from 'react';
import { Button, Modal, Popover, Space, message } from 'antd';
import { filmApi } from '../../services/api/film';
import type { EventPlan, FilmEvent } from '../../types/film';
import { apiErrorText } from './apiErrorText';
import { STRUCT_CONFIRM, STRUCT_DONE, mergeNextOf, splitChoicesOf, structDisabledReason } from './workflowStructOps';
import { needsConfirm } from './workflowConfirmPolicy';

interface Props {
  eventId: string;
  ev: FilmEvent;
  detail: EventPlan | null;
  editing: boolean;
  onActionDone: () => void;
}

const WorkflowEventStructBar: React.FC<Props> = ({ eventId, ev, detail, editing, onActionDone }) => {
  const [busy, setBusy] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const disabled = structDisabledReason({ editing, busy });
  const choices = useMemo(() => splitChoicesOf(ev), [ev]);
  const merge = useMemo(() => (detail ? mergeNextOf(detail.scenes, eventId) : { ok: false, reason: 'Chưa tải xong chi tiết plan.' }), [detail, eventId]);
  const beats = (ev.beatMeta?.actionBeats ?? []) as Array<{ order: number; text: string }>;

  const run = async (kind: keyof typeof STRUCT_CONFIRM, fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); message.success(STRUCT_DONE[kind]); }
    catch (e) { message.error(apiErrorText(e, STRUCT_CONFIRM[kind].title.replace(/\?$/, ''))); }
    finally { setBusy(false); onActionDone(); }
  };
  const ask = (kind: keyof typeof STRUCT_CONFIRM, fn: () => Promise<unknown>, extra?: string) => {
    const c = STRUCT_CONFIRM[kind];
    if (!needsConfirm('DIRECTING', kind === 'delete' ? 'DELETE' : kind === 'insert' ? 'INSERT' : 'STRUCT_CHANGE')) { void run(kind, fn); return; } // U9: chèn video trống không hỏi; cắt / gộp / xoá vẫn hỏi
    Modal.confirm({ title: c.title, content: extra ? `${extra} ${c.content}` : c.content, okText: c.okText, cancelText: 'Huỷ', okButtonProps: kind === 'delete' ? { danger: true } : undefined, onOk: () => run(kind, fn) });
  };

  const splitContent = (
    <div style={{ maxWidth: 320 }}>
      <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
        {choices.mode === 'beat' ? 'Cắt sau nhịp nào? Thoại đi theo nhịp đứng trước nó; shot hai nửa dàn lại ở bước Đạo diễn.' : 'Cắt sau shot nào? Thoại đi theo shot.'}
      </div>
      <Space wrap size={4}>
        {choices.points.map((k) => (
          <Button key={k} size="small" onClick={() => {
            setSplitOpen(false);
            const body = choices.mode === 'beat' ? { keepBeatCount: k } : { keepShotCount: k };
            const label = choices.mode === 'beat' ? `Cắt sau nhịp ${k}${beats[k - 1]?.text ? ` — ${String(beats[k - 1].text).slice(0, 30)}` : ''}.` : `Cắt sau shot ${k}.`;
            ask('split', () => filmApi.splitEvent(eventId, body), label);
          }}>{choices.mode === 'beat' ? `sau nhịp ${k}` : `sau shot ${k}`}</Button>
        ))}
      </Space>
    </div>
  );

  return (
    <div className="wf-struct" data-testid="wf-struct-bar" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '8px 0' }}>
      <span className="muted" style={{ fontSize: 12 }}>Cấu trúc video:</span>
      <Popover open={splitOpen && choices.points.length > 0 && !disabled} onOpenChange={(o) => setSplitOpen(o)} trigger="click" placement="bottomLeft" content={splitContent}>
        <Button size="small" disabled={!!disabled || choices.points.length === 0} title={disabled ?? (choices.points.length === 0 ? 'Video này không có ranh giới hợp lệ để cắt.' : 'Cắt đôi video khi quá dày cho một lượt gen.')} data-testid="wf-struct-split">✂️ Cắt đôi</Button>
      </Popover>
      <Button size="small" disabled={!!disabled || !merge.ok} title={disabled ?? (merge.ok ? 'Gộp với video kế tiếp cùng cảnh.' : merge.reason)} data-testid="wf-struct-merge"
        onClick={() => ask('merge', () => filmApi.mergeEventNext(eventId))}>⬇️ Gộp với video kế</Button>
      <Button size="small" disabled={!!disabled} title={disabled ?? 'Chèn một video trống ngay sau video này.'} data-testid="wf-struct-insert"
        onClick={() => ask('insert', () => filmApi.insertEventAfter(eventId))}>＋ Chèn video trống</Button>
      <Button size="small" danger disabled={!!disabled} title={disabled ?? 'Xoá video này kèm shot và thoại.'} data-testid="wf-struct-delete"
        onClick={() => ask('delete', () => filmApi.deleteEvent(eventId))}>Xoá video</Button>
      {disabled ? <span className="faint" style={{ fontSize: 12 }}>{disabled}</span> : null}
    </div>
  );
};

export default WorkflowEventStructBar;
